import { useState, useEffect, useMemo } from 'react';
import RestaurantCard from '../components/restaurant/RestaurantCard';
import RestaurantDetail from '../components/restaurant/RestaurantDetail';
import FiltersBar from '../components/restaurant/FiltersBar';
import { EmptyState, Spinner, Button } from '../components/ui/UI';
import { fetchRestaurants, getStatutForUser } from '../lib/api';
import { useAuth } from '../hooks/useAuth';

export default function HomePage({ onAddClick }) {
  const { user } = useAuth();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [filters, setFilters] = useState({ statut: 'tous', cuisine: 'tous', prix: 'tous', noteMin: '0', search: '', createur: 'tous' });

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try { setRestaurants(await fetchRestaurants()); }
    finally { setLoading(false); }
  }

  // Enrichit chaque restaurant avec le statut personnel de l'utilisateur connecté
  const restaurantsAvecStatut = useMemo(() =>
    restaurants.map(r => ({
      ...r,
      monStatut: user ? getStatutForUser(r, user.id) : null,
    })), [restaurants, user]);

  const filtered = useMemo(() => {
    return restaurantsAvecStatut.filter(r => {
      if (filters.statut !== 'tous' && r.monStatut !== filters.statut) return false;
      if (filters.cuisine !== 'tous' && r.type_cuisine !== filters.cuisine) return false;
      if (filters.prix !== 'tous' && String(r.niveau_prix) !== filters.prix) return false;
      if (filters.noteMin && filters.noteMin !== '0' && (!r.note_moyenne || r.note_moyenne < parseFloat(filters.noteMin))) return false;
      if (filters.createur && filters.createur !== 'tous' && r.added_by_profile?.username !== filters.createur) return false;
      if (filters.search) {
        const s = filters.search.toLowerCase();
        if (!r.nom.toLowerCase().includes(s) && !r.adresse.toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [restaurantsAvecStatut, filters]);

  const tested = restaurantsAvecStatut.filter(r => r.monStatut === 'teste').length;
  const toTest = restaurantsAvecStatut.filter(r => r.monStatut === 'tester').length;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 20px' }}>
      <div style={{ marginBottom: 32, animation: 'slideUp 0.4s ease' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(28px,5vw,42px)', fontWeight: 800, marginBottom: 8 }}>
          🍜 Nos restaurants
        </h1>
        <p style={{ color: 'var(--text-3)', fontSize: 16 }}>
          {user
            ? `${tested} testés par vous · ${toTest} à tester · ${restaurants.length} au total`
            : `${restaurants.length} restaurants`}
        </p>
      </div>

      <FiltersBar filters={filters} onChange={setFilters} restaurants={restaurants} />

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}><Spinner size={40} /></div>
      ) : filtered.length === 0 ? (
        <EmptyState icon="🍽️" title="Aucun restaurant trouvé" subtitle="Essayez de modifier vos filtres." action={<Button onClick={onAddClick}>+ Ajouter un restaurant</Button>} />
      ) : (
        <div className="stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
          {filtered.map(r => (
            <div key={r.id} className="animate-slideUp">
              <RestaurantCard restaurant={r} monStatut={r.monStatut} onClick={() => setSelectedId(r.id)} />
            </div>
          ))}
        </div>
      )}

      <RestaurantDetail restaurantId={selectedId} open={!!selectedId} onClose={() => setSelectedId(null)} onUpdate={load} />
    </div>
  );
}
